import {
    afterNextRender,
    Component,
    DestroyRef,
    ElementRef,
    inject,
    input,
    InputSignal,
    signal,
    WritableSignal,
} from '@angular/core';

import { ContentGroupSection } from '@core/cms/landing.cms';
import { ModuleChip } from '@shared/ui/module-chip/module-chip';
import { TrustHtmlPipe } from '@shared/pipes/trust-html/trust-html.pipe';

/**
 * Renders a `ComponentSectionContentGroup` using the home `app-modules` layout: intro content above
 * a grid of expandable chips whose body floats as an overlay dropdown (so opening one never reflows
 * the rows below). One chip is open at a time; outside-press or Escape closes it.
 */
@Component({
    selector: 'cms-content-group',
    host: {
        '(document:keydown.escape)': 'close()',
    },
    imports: [ModuleChip, TrustHtmlPipe],
    templateUrl: './content-group.html',
    styleUrl: './content-group.scss',
})
export class CmsContentGroup {
    private readonly _host: ElementRef<HTMLElement> = inject(ElementRef);
    private readonly _destroyRef: DestroyRef = inject(DestroyRef);

    public readonly section: InputSignal<ContentGroupSection> =
        input.required<ContentGroupSection>();

    protected readonly expandedId: WritableSignal<string | null> = signal(null);

    constructor() {
        // Close on outside press. A capture-phase listener is used so it still fires when an outer
        // element calls stopPropagation() during the bubble phase.
        afterNextRender((): void => {
            const onPointerDown = (event: Event): void => {
                if (!this.expandedId()) {
                    return;
                }

                const openCell: Element | null = this._host.nativeElement.querySelector(
                    '.cms-content-group__cell--expanded',
                );

                if (openCell && !openCell.contains(event.target as Node)) {
                    this.close();
                }
            };

            document.addEventListener('pointerdown', onPointerDown, true);
            this._destroyRef.onDestroy((): void =>
                document.removeEventListener('pointerdown', onPointerDown, true),
            );
        });
    }

    protected isExpanded(id: string): boolean {
        return this.expandedId() === id;
    }

    protected toggle(id: string): void {
        this.expandedId.update((current: string | null): string | null =>
            current === id ? null : id,
        );
    }

    protected close(): void {
        this.expandedId.set(null);
    }
}
